/**
 * D13: Teacher Practice Assignments (Huiswerk)
 * Source: Teacher portal — "Huiswerk opgeven" feature
 *
 * AS-2026-07-P2-05 (July 2026): mirrors `teacherAssignments.ts` — every
 * writer returns `Promise<boolean>` propagated from `writeJson` instead of
 * silently discarding it. A `false` result means the change did not persist
 * and must not be presented to the user as done.
 */

import { USE_BACKEND, readJson, writeJson } from '../helpers'
import type { PracticeAssignment } from '../../assessment'

const PRACTICE_ASSIGNMENTS_KEY = 'practice_assignments'

/** Get all teacher-created practice assignments */
export async function getPracticeAssignments(): Promise<
  Record<string, PracticeAssignment>
> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return readJson<Record<string, PracticeAssignment>>(
    PRACTICE_ASSIGNMENTS_KEY,
    {},
  )
}

/** Save or update a teacher practice assignment. Returns persistence success. */
export async function savePracticeAssignment(
  id: string,
  assignment: PracticeAssignment,
): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, PracticeAssignment>>(
    PRACTICE_ASSIGNMENTS_KEY,
    {},
  )
  all[id] = assignment
  return writeJson(PRACTICE_ASSIGNMENTS_KEY, all)
}

/** Delete a teacher practice assignment. Returns persistence success. */
export async function deletePracticeAssignment(id: string): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, PracticeAssignment>>(
    PRACTICE_ASSIGNMENTS_KEY,
    {},
  )
  delete all[id]
  return writeJson(PRACTICE_ASSIGNMENTS_KEY, all)
}

/** Archive a teacher practice assignment. Returns persistence success. */
export async function archivePracticeAssignment(id: string): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, PracticeAssignment>>(
    PRACTICE_ASSIGNMENTS_KEY,
    {},
  )
  const assignment = all[id]
  if (!assignment) return false
  assignment.status = 'archived'
  all[id] = assignment
  return writeJson(PRACTICE_ASSIGNMENTS_KEY, all)
}

/**
 * Mark a practice assignment as completed by a specific student.
 *
 * Returns `true` when the ledger reflects the completion after this call
 * (including the idempotent already-recorded case), `false` when the
 * assignment is missing or the write did not persist.
 */
export async function markPracticeAssignmentCompletedByStudent(
  assignmentId: string,
  studentId: string,
): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    PRACTICE_ASSIGNMENTS_KEY,
    {},
  )
  const assignment = all[assignmentId]
  if (!assignment) return false
  const completedBy = (assignment.completedByStudentIds as string[]) || []
  if (completedBy.includes(studentId)) return true
  assignment.completedByStudentIds = [...completedBy, studentId]
  return writeJson(PRACTICE_ASSIGNMENTS_KEY, all)
}
